import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { getValidAccessToken } from '../api/http.ts';
import type { JoinListResult } from './hubTypes.ts';

/**
 * The SignalR connection to the list hub.
 *
 * One connection per app instance, re-joining whichever list is open. A connection per
 * list would mean a new handshake every time someone switches list, which on a phone
 * with poor signal is exactly when you least want one.
 */
export function createListConnection(): HubConnection {
  return new HubConnectionBuilder()
    .withUrl('/hubs/list', {
      // Browsers cannot set an Authorization header on a WebSocket handshake, so the
      // client sends the token as ?access_token=. The server only honours that on the
      // hub path — see RealtimeExtensions.
      //
      // Asynchronous, and deliberately not a read of the token store: SignalR calls this
      // again for every reconnect attempt, and those attempts are mostly made by a phone
      // that has been asleep for longer than an access token lives. Refreshing here is
      // what stops a reconnect loop retrying for ever with a token the server has already
      // rejected — see getValidAccessToken.
      accessTokenFactory: async () => (await getValidAccessToken()) ?? '',
      // WebSockets first; SignalR falls back on its own where a proxy blocks them.
      transport:
        HttpTransportType.WebSockets |
        HttpTransportType.ServerSentEvents |
        HttpTransportType.LongPolling,
    })
    // Retry roughly every few seconds indefinitely rather than SignalR's default of
    // giving up after four attempts. A shopper walks out of a dead spot on their own
    // schedule, and the app must be live again when they do, without a manual reload.
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (context) => {
        if (context.elapsedMilliseconds < 60_000) {
          return 2_000;
        }
        // Back off once it is clearly a longer outage, to save battery.
        return 10_000;
      },
    })
    .configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning)
    .build();
}

/**
 * Joins a list group, returning who else has it open.
 *
 * Returns null when the server refuses — which happens legitimately if membership was
 * revoked while the app was open, so callers should treat it as "stop showing this
 * list" rather than as an error to retry.
 */
export async function joinList(
  connection: HubConnection,
  listId: string,
): Promise<JoinListResult | null> {
  if (connection.state !== HubConnectionState.Connected) {
    return null;
  }

  try {
    const result = await connection.invoke<JoinListResult>('JoinList', listId);
    return result.success ? result : null;
  } catch {
    return null;
  }
}

export async function leaveList(connection: HubConnection, listId: string): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) {
    return;
  }

  try {
    await connection.invoke('LeaveList', listId);
  } catch {
    // Leaving is best-effort: if the connection has already dropped, the server's
    // disconnect handler cleans presence up anyway.
  }
}
